import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
  Button
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getJobOrder } from "../API/jobOrderAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from "@mui/icons-material/Circle";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function ViewTransmittalForm() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');
  const [jobOrder, setJobOrder] = useState(null);

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  useEffect(() => {
    if (!id) return;
    let isActive = true;
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        if (isActive) setJobOrder(data || {});
      } catch (err) {
        if (isActive) setErrorMessage(`Error fetching job order${err?.message ? `: ${err.message}` : ""}`);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    fetchJobOrder();
    return () => { isActive = false; };
  }, [id]);

  if (!jobOrder) return null;

  //   const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        {/* Basic Information Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6">
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <TextField
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <TextField
                    label="Job Order Status"
                    value={jobOrder?.status || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Client ID"
                    value={jobOrder?.client?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={12} xl={10}>
                      <TextField
                        label="Job Site Address"
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder.client.address.house_unit_street || ""}, ${jobOrder.client.address.barangay || ""}, ${jobOrder.client.address.city || ""}, ${jobOrder.client.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                            : ""
                        }
                        fullWidth
                        disabled
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} xl={2}>
                      <TextField
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Person"
                    value={
                      jobOrder?.client?.contact
                        ? `${jobOrder.client.contact.lastname}, ${jobOrder.client.contact.firstname}`
                        : ""
                    }
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Email"
                    value={jobOrder?.client?.contact?.email?.email || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Number"
                    value={
                      jobOrder?.client?.contact?.number
                        ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                        : ""
                    }
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={4} xl={4}>
                  <TextField
                    label="Location Type"
                    value={jobOrder?.client?.location_type || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={jobOrder?.special_notes || ""}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Transmittal Notes */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Notes"
                value={jobOrder.transmittal_notes || ''}
                fullWidth multiline rows={6}
                disabled size="small" />
            </CardContent>
          </Card>
        </Grid>

        {/* Service Accomplished by Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Service Accomplished by:
              </Typography>
              <Grid container spacing={2}>
                { (jobOrder.accomplished_by_technician || jobOrder.technicians || [])
                    .map((name, i) =>
                      typeof name === "string"
                        ? name
                        : `${name.firstname} ${name.lastname}`
                    )
                    .map((techName, i) => (
                  <Grid key={i} item xs={12}>
                    <TextField
                      label={`Technician ${i + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={techName}
                      disabled
                    />
                  </Grid>
                ))}

                {/* Accomplished Signature */}
                <Grid item xs={12}>
                  {jobOrder.accomplishedSignature && (
                    <Box
                      component="img"
                      src={jobOrder.accomplishedSignature}
                      alt="Accomplished Signature"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        objectFit: "contain",
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                      }}
                    />
                  )}
                </Grid>

                {/* Technician Signatory dropdown, right below the signature */}
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="technician-signatory-label">
                      Technician Signatory
                    </InputLabel>
                    <Select
                      labelId="technician-signatory-label"
                      value={jobOrder.technician_signatory || ""}
                      label="Technician Signatory"
                      disabled
                    >
                      {jobOrder.technicians?.map((tech) => (
                        <MenuItem
                          key={tech.id}
                          value={`${tech.firstname} ${tech.lastname}`}
                        >
                          {`${tech.firstname} ${tech.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder.technician_signature_uploaded_at && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        🔵 Technician Signature Uploaded At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                        {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrder?.job_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrder?.start_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                {/* <Grid item xs={12} md={4}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrder?.end_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrder?.end_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Acknowledged Signature Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For Client Only
                </Typography>
              }
              sx={{ px: 2, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Acknowledged By:
              </Typography>
              {jobOrder.acknowledgedSignature && (
                <Box
                  component="img"
                  src={jobOrder.acknowledgedSignature}
                  alt="Acknowledged Signature"
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    objectFit: "contain",
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                  }}
                />
              )}
              <TextField
                label="Client Representative"
                variant="outlined"
                fullWidth
                size="small"
                value={jobOrder.acknowledgedClientSignatory || ""}
                disabled
              />

              {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
              {!isTechnician && jobOrder.client_signature_uploaded_at && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🟢 Client Signature Uploaded At:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {jobOrder?.receipts_url ? (
                  <>
                    {jobOrder?.receipts_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.receipts_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewTransmittalForm;